'use client';

import { useTranslation } from '@/lib/i18n';
import { cn, currencyFormatter } from '@/lib/utils';
import { Skeleton } from '@mui/material';
import { CurrencyText } from '../cards/CurrencyText';
import usePolyPortfolio from '../hooks/use-poly-portfolio';
import { ChangeText } from '../cards/ChangeText';
import { isNumber } from 'lodash';
import { DepositWithdrawalActions } from '../deposit-withdrawal';
import useTransactionRecords from '../hooks/use-transaction';
import UserScan from '@/components/Icons/UserScan';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';
import { useSnackbar } from '@/contexts/SnackbarContext';
import useUserAsset from '../hooks/use-user-asset';
import useValueKline from '../hooks/use-value-kline';

interface AccountAssetProps {
  totalValue: number;
  pnlPercent: number;
  pnl: number;
  dir?: 'vertical' | 'horizontal';
}
function AccountAsset({ totalValue, pnlPercent, pnl, dir = 'horizontal' }: AccountAssetProps) {
  const { t } = useTranslation();
  const { wallets } = useTrackingWallets();
  const systemWallet = wallets.systemWallet;
  const { showSuccess } = useSnackbar();

  const index = pnlPercent > 0 ? 2 : pnlPercent < 0 ? 1 : 0;

  const copySystemWallet = async () => {
    if (!systemWallet) return;
    try {
      await navigator.clipboard.writeText(systemWallet);
      showSuccess(t('Wallet address copied successfully'));
    } catch {
      // 剪贴板不可用（如非 https）时静默失败
    }
  };

  if (dir === 'vertical') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5">
          <div className="text-2xl font-semibold leading-8.5 text-text-primary">
            {currencyFormatter(totalValue, {
              notation: totalValue > 10000 ? 'compact' : 'standard',
            })}
          </div>

          {systemWallet && (
            <button
              type="button"
              aria-label={t('Copy wallet address')}
              onClick={copySystemWallet}
              className="flex shrink-0 cursor-pointer items-center justify-center"
            >
              <UserScan size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2.5 text-sm leading-5">
          <span
            className={cn('font-normal', {
              'text-[#14C784]': index === 2,
              'text-[#FF6B6B]': index === 1,
              'text-[#757575]': index === 0,
            })}
          >
            <ChangeText
              value={pnlPercent}
              digits={2}
              showTriangle={false}
              className="text-sm font-normal"
            />
            (
            <CurrencyText
              value={pnl}
              digits={2}
              showTriangle={false}
              className="text-sm font-normal"
            />
            )
          </span>
          <span className="font-normal text-[#757575]">{t('24h')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-3">
      <div className="text-2xl text-text-primary">
        {currencyFormatter(totalValue, {
          notation: totalValue > 10000 ? 'compact' : 'standard',
        })}
      </div>
      <div className="flex items-baseline gap-1.5 text-sm ">
        <CurrencyText value={pnl} digits={2} showTriangle={false} />
        <span
          className={cn({
            'text-[#14C784]': index === 2,
            'text-[#FF6B6B]': index === 1,
            'text-[#757575]': index === 0,
          })}
        >
          (<ChangeText value={pnlPercent} digits={2} />)
        </span>
        <span className="font-normal text-[#757575]">{t('24h')}</span>
      </div>
    </div>
  );
}
export interface AssetOverviewProps extends Pick<AccountAssetProps, 'dir'> {
  trackedWallets: string[];
  type?: 'portfolio' | 'tracking';
}

/**
 * 资产概览
 */
export function AssetOverview({ trackedWallets, type, dir }: AssetOverviewProps) {
  const { points, isLoading } = useValueKline({
    wallets: trackedWallets,
    interval: '24h',
  });

  const { currentValue, isLoading: isAssetLoading } = useUserAsset({
    wallets: trackedWallets,
  });

  const assetLoading = isAssetLoading || isLoading;

  const { records } = useTransactionRecords();

  const firstValue = points.at(0)?.value;
  const lastValue = points.at(-1)?.value;
  const pnl = isNumber(firstValue) && isNumber(lastValue) ? lastValue - firstValue : 0;
  const pnlPercent = firstValue && firstValue > 0 ? (pnl / firstValue) * 100 : 0;

  return (
    <div className="flex justify-between items-end">
      {assetLoading ? (
        <Skeleton variant="rectangular" width={120} height={32} sx={{ bgcolor: 'grey.800' }} />
      ) : (
        <AccountAsset totalValue={currentValue} pnlPercent={pnlPercent} pnl={pnl} dir={dir} />
      )}
      {type === 'portfolio' && (
        <DepositWithdrawalActions
          showHistoryButton={records.length > 0}
          historyProps={{ telegramUrl: 'https://telegram.me/+J90_iHI_DpY1Yjcx' }}
        />
      )}
    </div>
  );
}
